import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Mic, Square, Loader2, Keyboard, PhoneOff, Volume2, Shield, Send, ExternalLink, MicOff, ScanLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useVoiceEngine } from "@/hooks/useVoiceEngine";
import { CameraScanner } from "@/components/CameraScanner";

const VOICE_LANGS = [{ c: "en", n: "English" }, { c: "es", n: "Español" }, { c: "pt", n: "Português" }];

export default function Voice() {
  const { t, lang, setLang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [transcript, setTranscript] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [textMode, setTextMode] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [showMicHelp, setShowMicHelp] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const documentId = location.state?.documentId;
  const [activeDocId, setActiveDocId] = useState(location.state?.documentId || null);
  const activeDocRef = useRef(activeDocId);
  useEffect(() => { activeDocRef.current = activeDocId; }, [activeDocId]);
  const scrollRef = useRef();
  const sessionRef = useRef(null);
  const langRef = useRef(lang);
  useEffect(() => { langRef.current = lang; }, [lang]);
  useEffect(() => { sessionRef.current = sessionId; }, [sessionId]);

  const inIframe = typeof window !== "undefined" && window.self !== window.top;

  const askAI = useCallback(async (text) => {
    setTranscript((m) => [...m, { role: "user", content: text, time: new Date() }]);
    const res = await api.post("/voice/chat", {
      message: text, language: langRef.current, session_id: sessionRef.current, document_id: activeDocRef.current,
    });
    if (!sessionRef.current) { sessionRef.current = res.data.session_id; setSessionId(res.data.session_id); }
    const answer = res.data.answer;
    setTranscript((m) => [...m, { role: "assistant", content: answer, time: new Date() }]);
    return answer;
  }, []);

  const ttsFetch = useCallback(async (text) => {
    const res = await fetch(`${API}/voice/tts`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ text, voice: "nova", speed: 1.0 }),
    });
    if (!res.ok) throw new Error("tts");
    return res.arrayBuffer();
  }, []);

  const onError = useCallback((code) => {
    const [kind, detail] = String(code).split(":");
    if (kind === "insecure") { toast.error(t("voice_secure_ctx")); setTextMode(true); }
    else if (kind === "unsupported") { toast.error(t("voice_no_access")); setTextMode(true); }
    else if (kind === "policy") { toast.error(t("voice_policy_blocked")); setTextMode(true); }
    else if (kind === "denied") {
      if (inIframe) { toast.error(t("voice_perm_blocked_iframe")); setTextMode(true); }
      else setShowMicHelp(true);
    }
    else if (kind === "notfound") { toast.error(t("voice_no_device")); setTextMode(true); }
    else if (kind === "busy") { toast.error(t("voice_in_use")); }
    else { toast.error(t("voice_start_generic", { err: detail || "unknown error" })); setTextMode(true); }
  }, [inIframe, t]);

  const engineRef = useRef(null);

  const onUtterance = useCallback(async (blob, ext) => {
    try {
      const fd = new FormData();
      fd.append("file", blob, `audio.${ext}`);
      fd.append("language", langRef.current);
      const tr = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const text = (tr.data.text || "").trim();
      if (!text) { toast.message(t("voice_no_catch")); engineRef.current?.end?.(); return; }
      const answer = await askAI(text);
      const buf = await ttsFetch(answer);
      await engineRef.current?.playAudio?.(buf, "audio/mpeg");
    } catch {
      toast.error(t("voice_process_err"));
      engineRef.current?.end?.();
    }
  }, [askAI, ttsFetch, t]);

  const engine = useVoiceEngine({ onUtterance, onError });
  engineRef.current = engine;
  const { phase, level, audioElRef, toggle, end } = engine;

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [transcript, phase]);

  const sendText = async (e) => {
    e?.preventDefault();
    if (!textInput.trim()) return;
    const v = textInput.trim(); setTextInput("");
    try { await askAI(v); } catch { toast.error(t("voice_response_err")); }
  };

  const endCall = () => { end(); navigate("/dashboard"); };

  const submitVoiceScan = async (files) => {
    setScanning(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("language", langRef.current);
    if (sessionRef.current) fd.append("session_id", sessionRef.current);
    try {
      const res = await api.post("/voice/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!sessionRef.current) { sessionRef.current = res.data.session_id; setSessionId(res.data.session_id); }
      if (res.data.document_id) setActiveDocId(res.data.document_id);
      setScanOpen(false);
      const answer = res.data.answer;
      setTranscript((m) => [...m, { role: "assistant", content: answer, time: new Date() }]);
      try {
        const buf = await ttsFetch(answer);
        await engineRef.current?.playAudio?.(buf, "audio/mpeg");
      } catch { /* playback best-effort */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("scan_failed"));
    } finally {
      setScanning(false);
    }
  };

  const orbLabel = phase === "listening" ? t("listening")
    : phase === "processing" || phase === "requesting" ? t("thinking")
    : phase === "speaking" ? t("speaking")
    : t("tap_speak");

  const listening = phase === "listening";
  const busy = phase === "processing" || phase === "requesting";
  const ringScale = 1 + Math.min(0.35, level * 0.5);

  return (
    <AppLayout>
      <audio ref={audioElRef} className="hidden" playsInline />
      <div className="mx-auto max-w-2xl">
        <div className="text-center">
          <h1 className="font-display text-3xl font-semibold text-navy">{t("talk_ai")}</h1>
          <p className="mt-1 text-slate-500">{t("voice_sub")}</p>
        </div>

        <div className="mt-5 flex justify-center">
          <div className="inline-flex rounded-full bg-slate-100 p-1" role="tablist" aria-label="Voice language">
            {VOICE_LANGS.map((l) => (
              <button key={l.c} data-testid={`voice-lang-${l.c}`} onClick={() => setLang(l.c)}
                aria-selected={lang === l.c}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${lang === l.c ? "bg-white text-brandblue card-shadow" : "text-slate-500"}`}>{l.n}</button>
            ))}
          </div>
        </div>

        {/* Orb */}
        <div className="mt-8 flex flex-col items-center">
          <div className="relative flex h-40 w-40 items-center justify-center">
            {listening && (
              <span className="absolute rounded-full bg-brandteal/20 transition-transform duration-100"
                style={{ height: "8rem", width: "8rem", transform: `scale(${ringScale})` }} aria-hidden="true" />
            )}
            <button data-testid="voice-orb"
              onClick={toggle}
              disabled={busy}
              aria-label={listening ? "Stop listening" : phase === "speaking" ? "Interrupt and speak" : "Start voice conversation"}
              className={`relative flex h-32 w-32 items-center justify-center rounded-full brand-gradient text-white card-shadow-lg transition-transform ${listening ? "orb-pulse" : ""} ${busy ? "opacity-90" : "hover:scale-[1.03]"}`}>
              {busy ? <Loader2 className="h-10 w-10 animate-spin" />
                : phase === "speaking" ? <Volume2 className="h-10 w-10 animate-pulse" />
                : listening ? <Square className="h-9 w-9" />
                : <Mic className="h-10 w-10" />}
            </button>
          </div>
          <p className="mt-4 font-medium text-brandblue" data-testid="voice-status" aria-live="polite">{orbLabel}</p>
          {listening && <p className="text-xs text-slate-400">{t("tap_stop")}</p>}
          {phase === "speaking" && <p className="text-xs text-slate-400">{t("tap_speak")}</p>}
        </div>

        {/* Transcript */}
        <div ref={scrollRef} className="mt-8 space-y-3 max-h-[36vh] overflow-y-auto" data-testid="voice-transcript">
          {transcript.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && <span className="mr-2 mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-blue-50 text-brandblue"><Shield className="h-3.5 w-3.5" /></span>}
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-blue-50 text-navy" : "bg-white border border-slate-100 text-slate-700"}`}>
                <p className="whitespace-pre-wrap">{m.content}</p>
              </div>
            </div>
          ))}
        </div>

        {textMode && (
          <form onSubmit={sendText} className="mt-4 flex gap-2 rounded-2xl bg-white border border-slate-100 card-shadow p-2">
            <input data-testid="voice-text-input" value={textInput} onChange={(e) => setTextInput(e.target.value)} placeholder={t("ask_placeholder")}
              aria-label={t("ask_placeholder")} className="flex-1 bg-transparent px-3 py-2 focus:outline-none" />
            <button type="submit" aria-label={t("send")} className="rounded-xl brand-gradient text-white px-4"><Send className="h-4 w-4" /></button>
          </form>
        )}

        {/* Controls */}
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button data-testid="voice-scan-btn" onClick={() => setScanOpen(true)}
            className="inline-flex items-center gap-2 rounded-full border border-brandteal/40 bg-brandteal/5 px-4 py-2.5 text-sm font-medium text-navy hover:bg-brandteal/10">
            <ScanLine className="h-4 w-4 text-brandteal" /> {t("voice_scan")}
          </button>
          <button data-testid="voice-switch-text" onClick={() => setTextMode((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-navy hover:bg-slate-50">
            <Keyboard className="h-4 w-4" /> {textMode ? t("switch_voice") : t("switch_text")}
          </button>
          {inIframe && (
            <a data-testid="voice-newtab" href={window.location.href} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-navy hover:bg-slate-50">
              <ExternalLink className="h-4 w-4" /> {t("open_new_tab")}
            </a>
          )}
          <button data-testid="voice-end" onClick={endCall}
            className="inline-flex items-center gap-2 rounded-full brand-gradient px-5 py-2.5 text-sm font-medium text-white">
            <PhoneOff className="h-4 w-4" /> {t("end_call")}
          </button>
        </div>
        {inIframe && (
          <p className="mt-3 text-center text-xs text-slate-400">{t("voice_mic_hint")}</p>
        )}
      </div>

      <CameraScanner open={scanOpen} onClose={() => setScanOpen(false)} onSubmit={submitVoiceScan}
        submitting={scanning} readingLabel={t("voice_scanning")} />

      <Dialog open={showMicHelp} onOpenChange={setShowMicHelp}>
        <DialogContent data-testid="mic-help-dialog">
          <DialogHeader>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-brandblue mb-1"><MicOff className="h-6 w-6" /></div>
            <DialogTitle className="font-display text-2xl text-navy">{t("mic_allow_title")}</DialogTitle>
            <DialogDescription className="text-slate-500">{t("mic_allow_intro")}</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-amber-50/70 border border-amber-100 px-4 py-3 text-sm text-amber-700">{t("mic_denied_note")}</div>
          <ol className="mt-1 space-y-3">
            {[t("mic_step_1"), t("mic_step_2"), t("mic_step_3")].map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full brand-gradient text-white text-xs font-bold">{i + 1}</span>
                <span className="text-sm text-slate-700 leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Button data-testid="mic-help-retry" onClick={() => { setShowMicHelp(false); engine.start(); }}
              className="flex-1 rounded-full brand-gradient text-white"><Mic className="h-4 w-4 mr-2" />{t("mic_try_again")}</Button>
            <Button data-testid="mic-help-text" variant="outline" onClick={() => { setShowMicHelp(false); setTextMode(true); }}
              className="flex-1 rounded-full"><Keyboard className="h-4 w-4 mr-2" />{t("mic_use_text")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
